import { useMemo } from 'react'
import { Link, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { say, useDocumentVersions } from '../data/plan.ts'
import { procedureOrigin } from '../data/planUi.ts'
import { illustrationsOf, type IllustrationLink } from '../data/procedurePhotos.ts'
import { IllustrationImage } from '../photos/Illustration.tsx'
import { useBlobs, useEntityPhotos } from '../photos/usePhotos.ts'
import { useClub } from '../club/ClubProvider.tsx'
import { useAcknowledged } from '../data/acknowledgements.ts'
import { formatLocal } from '../lib/time.ts'
import { BackLink, Banner, Pill } from '../ui/components.tsx'
import { useAction } from '../ui/hooks.ts'
import { Picto } from '../ui/pictos.tsx'
import { Steps } from './PlanJob.tsx'

/** A procedure as the crew read it: the steps of a job (how to mow a green, how to rake a bunker). From the device's own copy, so it opens in the
 *  middle of the course with no signal. An older version stays readable: the work done under it points at it. M16-I1: the version's pictures with their
 *  captions (the mowing pattern), from the device's cache when there is no signal (the lane fetches the assigned jobs' pictures ahead). M16-I2: under the
 *  steps, "J'ai lu cette version" once (a field fact, offline), then the day it was read. */
export function ProcedurePage() {
  const { t, i18n } = useTranslation()
  const { versionId } = useParams({ strict: false }) as { versionId: string }
  const search = useSearch({ strict: false }) as { from?: string }
  const versions = useDocumentVersions()
  const lang = i18n.language
  // the way back is where the page was opened from (review of item 47: the timer, the job), else the planning
  const origin = procedureOrigin(search.from)
  const back = origin ? <BackLink to={origin.to as never} params={origin.params} testId="procedure-back">← {t('common.back')}</BackLink> : <BackLink to="/plan" testId="procedure-back">← {t('plan.back')}</BackLink>
  if (!versions) return <p>{t('common.loading')}</p>
  const v = versions.find((x) => x.id === versionId)
  if (!v) return <section className="flex flex-col gap-3">{back}<Banner tone="warn"><span data-testid="procedure-missing">{t('plan.procedureMissing')}</span></Banner></section>
  const siblings = versions.filter((x) => x.document_id === v.document_id).sort((a, b) => b.version_no - a.version_no)
  const newest = siblings[0]!
  return (
    <section className="flex flex-col gap-4" data-testid="procedure-page">
      {back}
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-bold" data-testid="procedure-title">{say(v.title, lang)}</h1>
        <Pill tone={v.status === 'published' ? 'green' : 'gray'}>{t('plan.version', { n: v.version_no })}</Pill>
        {v.status === 'retired' && <Pill tone="amber">{t('plan.procedureOld')}</Pill>}
      </div>
      {v.status === 'retired' && <Link to="/procedures/$versionId" params={{ versionId: newest.id }} className="btn w-full" data-testid="procedure-newest">{t('plan.procedureOpenNewest', { n: newest.version_no })}</Link>}
      {v.summary && <p className="meta">{say(v.summary, lang)}</p>}
      <Illustrations versionId={v.id} />
      <div className="card"><Steps text={say(v.body, lang)} /></div>
      <ReadThisVersion versionId={v.id} />
      {siblings.length > 1 && (
        <details>
          <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-2 font-semibold [&::-webkit-details-marker]:hidden">{t('plan.procedureHistory', { count: siblings.length })}<Picto name="chevron" className="size-5 shrink-0 rotate-90 opacity-50" /></summary>
          <ul className="flex flex-col gap-2">{siblings.map((s) => <li key={s.id}><Link to="/procedures/$versionId" params={{ versionId: s.id }} className="btn w-full justify-between">{t('plan.version', { n: s.version_no })}<span className="meta">{s.effective_from.split('-').reverse().join('/')}</span></Link></li>)}</ul>
        </details>
      )}
    </section>
  )
}

/** The version's pictures in the order they were added, each with its caption; a picture this device took and still holds is shown from its own copy. Nothing when the
 *  version has none (most procedures are words only). */
function Illustrations({ versionId }: { versionId: string }) {
  const { t } = useTranslation()
  const links = useEntityPhotos(versionId)
  const blobs = useBlobs()
  const shown = useMemo(() => illustrationsOf(links as unknown as IllustrationLink[], versionId), [links, versionId])
  if (shown.length === 0) return null
  return (
    <section className="flex flex-col gap-3" data-testid="procedure-illustrations" data-count={shown.length}>
      <h2 className="text-lg font-bold">{t('plan.illustrations')}</h2>
      <ul className="flex flex-col gap-4">
        {shown.map((l, i) => (
          <li key={l.id}>
            <figure className="flex flex-col gap-1" data-testid="procedure-illustration" data-sha={l.sha256}>
              <IllustrationImage link={l} local={blobs.get(l.sha256)?.blob} alt={l.caption ?? t('photos.alt')} className="w-full rounded-xl border border-slate-200 bg-white object-contain dark:border-night-line" boxClassName="aspect-[4/3] w-full rounded-xl border border-slate-200 dark:border-night-line" testId="illustration" />
              <figcaption className="text-sm" data-testid="illustration-caption"><span className="font-semibold">{i + 1}.</span> {l.caption ?? ''}</figcaption>
            </figure>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** M16-I2: "J'ai lu cette version", once, by the person acting on this device (the workstation's unlocked person under his PIN): a field fact, offline-first,
 *  the version shown being the one read (an older version too: the job may point at it). Then the day it was read, pending until the server has it. A
 *  procedure that asks for a reading says so above the button; a reading is not a competence, and the page says that as well. Nothing without a person. */
function ReadThisVersion({ versionId }: { versionId: string }) {
  const { t } = useTranslation()
  const { send, site, can } = useClub()
  const a = useAcknowledged(versionId)
  const { busy, error, run } = useAction()
  if (!a.ready || !a.personId || !can('procedure.read')) return null
  const person = a.personId
  if (a.reading) {
    return (
      <section className="card flex flex-col gap-1" data-testid="procedure-read" data-pending={a.reading._pending ? '1' : '0'}>
        <p className="font-semibold" data-testid="procedure-read-at">✓ {t('ack.readOn', { when: formatLocal(a.reading.acknowledged_at, site.timezone, 'dd/MM/yyyy HH:mm') })}</p>
        {a.reading._pending && <p className="meta" data-testid="procedure-read-pending">{t('common.pendingSend')}</p>}
        <p className="meta">{t('ack.notCompetence')}</p>
      </section>
    )
  }
  return (
    <section className="card flex flex-col gap-2" data-testid="procedure-ack-card" data-asks={a.asks ? '1' : '0'}>
      {a.asks && <p className="flex flex-wrap items-center gap-2"><Pill tone="amber">{t('ack.unread')}</Pill><span className="meta">{t('ack.asked')}</span></p>}
      <button className="btn btn-primary" disabled={busy} onClick={() => void run(async () => { await send('procedure.acknowledge@1', { document_version_id: versionId, person_id: person }) })} data-testid="procedure-ack">{t('ack.button')}</button>
      <p className="meta">{t('ack.help')}</p>
      {error && <Banner tone="error"><span data-testid="procedure-ack-error">{error}</span></Banner>}
    </section>
  )
}
